import { useCreateFoodSupplement } from '@/api/food-supplement/food-supplement.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { CreateFoodSupplementRequest } from '@repo/types/src/food-supplement';
import { CirclePlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  taste: '',
  dosagePerDay: '',
  descriptionTitle: '',
  description: '',
  image: '',
  link: '',
  fastActing: false,
};

export const AddFoodSupplement = ({ refetch }: { refetch: () => void }) => {
  const mutation = useCreateFoodSupplement();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const handleCreate = () => {
    if (!formData.name) {
      toast.error('Le nom est obligatoire');
      return;
    }

    const payload: CreateFoodSupplementRequest = {
      name: formData.name,
      ...(formData.taste && { taste: formData.taste }),
      ...(formData.dosagePerDay && {
        dosagePerDay: parseInt(formData.dosagePerDay, 10),
      }),
      ...(formData.descriptionTitle && {
        descriptionTitle: formData.descriptionTitle,
      }),
      ...(formData.description && { description: formData.description }),
      ...(formData.image && { image: formData.image }),
      ...(formData.link && { link: formData.link }),
      fastActing: formData.fastActing,
    };

    mutation.mutate(payload, {
      onSuccess: (data) => {
        setOpen(false);
        setFormData(emptyForm);
        refetch();
        toast.success(`${data.name} a été créé avec succès!`, {
          duration: 3000,
        });
      },
      onError: () => {
        toast.error(`Erreur lors de la création du complément alimentaire`, {
          duration: 3000,
        });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button>
          <div className="flex flex-row gap-2 items-center">
            <CirclePlus size={24} />
            <span>Ajouter un complément</span>
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">
            Ajouter un complément alimentaire
          </DialogTitle>
          <DialogDescription className="text-center">
            Créer un nouveau complément alimentaire
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-3">
          <Input
            type="text"
            placeholder="Nom *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <Input
            type="text"
            placeholder="Goût"
            value={formData.taste}
            onChange={(e) =>
              setFormData({ ...formData, taste: e.target.value })
            }
          />
          <Input
            type="number"
            placeholder="Dosage par jour"
            value={formData.dosagePerDay}
            min={1}
            onChange={(e) =>
              setFormData({ ...formData, dosagePerDay: e.target.value })
            }
          />
          <Input
            type="text"
            placeholder="Titre de la description"
            value={formData.descriptionTitle}
            onChange={(e) =>
              setFormData({ ...formData, descriptionTitle: e.target.value })
            }
          />
          <Input
            type="text"
            placeholder="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <Input
            type="text"
            placeholder="URL de l'image"
            value={formData.image}
            onChange={(e) =>
              setFormData({ ...formData, image: e.target.value })
            }
          />
          <Input
            type="url"
            placeholder="Lien"
            value={formData.link}
            onChange={(e) => setFormData({ ...formData, link: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={formData.fastActing}
              onChange={(e) =>
                setFormData({ ...formData, fastActing: e.target.checked })
              }
            />
            Action rapide
          </label>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost">Annuler</Button>
          </DialogClose>
          <Button
            variant="default"
            onClick={handleCreate}
            disabled={mutation.isPending}
          >
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
